import React from 'react';

import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import styled, { css } from '../../styled';
import { BREAKPOINTS_VALUES, colorsPalette } from '../../theme';
import { LazyLoadImage } from '../LazyloadImage';
import { CategoryLabel } from '../UI/CategoryLabel';
import { ProgressLine } from '../UI/ProgressLine';

interface PathwayInterface {
  videoWidth: number | null;
  background?: string | null;
  handleClick?: (id: string) => void;
  id?: string | null;
  name?: string | null;
  description?: string | null;
  imageUrl?: string | null;
  duration?: string | null;
  countVideos?: number | null;
  slug?: string | null;
  progress?: number;
  className?: string;
  withHover?: boolean;
}

const Pathway: React.FC<PathwayInterface> = ({
  videoWidth,
  background,
  name,
  imageUrl,
  description,
  duration,
  countVideos,
  slug,
  progress,
  className,
  withHover = true,
}) => {
  const { t } = useTranslation();
  const videosCount = countVideos ? `${countVideos} ${countVideos > 1 ? 'videos' : 'video'}` : '';
  const link = `/pathway/${slug}`;

  return (
    <PathwayWrap
      data-testid="pathwayCard"
      className={className}
      background={background}
      withHover={withHover}
    >
      <Wrap>
        {/*TODO change to <Link /> when pathway page will be made*/}
        {slug ? (
          <Link data-testid="imageWrapperLink" to={link}>
            <ImgWrap
              className="imgWrap"
              withHover={withHover}
              withLink={slug}
              background={background}
            >
              {imageUrl && (
                <StyledLazyLoadImage
                  path={imageUrl}
                  alt={name}
                  draggable={false}
                  srcset={[{ width: 420 }, { width: 370 }, { width: 350 }, { width: 340 }]}
                  sizes={`(max-width:${BREAKPOINTS_VALUES['sm']}px) 420px, (max-width: ${BREAKPOINTS_VALUES['md']}) 370px, (max-width:${BREAKPOINTS_VALUES['lg']}px) 350px, 340px`}
                  data-testid="pathwayImage"
                  wrapperCss={imageLazyWrapperCss}
                />
              )}
            </ImgWrap>
          </Link>
        ) : (
          <ImgWrap
            className="imgWrap"
            withHover={withHover}
            withLink={slug}
            background={background}
          >
            {imageUrl && (
              <StyledLazyLoadImage
                path={imageUrl}
                alt={name}
                draggable={false}
                srcset={[{ width: 420 }, { width: 370 }, { width: 350 }, { width: 340 }]}
                sizes={`(max-width:${BREAKPOINTS_VALUES['sm']}px) 420px, (max-width: ${BREAKPOINTS_VALUES['md']}) 370px, (max-width:${BREAKPOINTS_VALUES['lg']}px) 350px, 340px`}
                wrapperCss={imageLazyWrapperCss}
              />
            )}
          </ImgWrap>
        )}
        <CatDifWrap>
          <CategoryLabel
            name={t('button.pathway')}
            bgColor={colorsPalette.pink}
            textColor={colorsPalette.blueDark}
          />
        </CatDifWrap>
        <TextWrap>
          {/*TODO change to <Link /> when pathway page will be made*/}
          {slug ? (
            <StyledLink data-testid="titleLink" to={link}>
              <Title
                variant="h3"
                component="h3"
                className="title"
                videoWidth={videoWidth}
                withHover={withHover}
                withLink={slug}
              >
                {name}
              </Title>

              {description && <Description className="description">{description}</Description>}
            </StyledLink>
          ) : (
            <>
              <Title
                variant="h3"
                component="h3"
                className={'title'}
                videoWidth={videoWidth}
                withHover={withHover}
                withLink={slug}
              >
                {name}
              </Title>

              {description && <Description className={'description'}>{description}</Description>}
            </>
          )}
          <Duration data-testid="duration">{videosCount + ' • ' + duration}</Duration>
          {(progress || progress === 0) && <ProgressLine width={progress} />}
        </TextWrap>
      </Wrap>
    </PathwayWrap>
  );
};

export default Pathway;

const PathwayWrap = styled('div', {
  shouldForwardProp: (prop) => prop !== 'background' && prop !== 'withHover',
})`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  height: 240px;
  border-radius: 5px;
  background: ${({ background, theme }) =>
    background === 'Dark' ? theme.palette.secondary.main : theme.palette.secondary.dark};

  &::before,
  &::after {
    width: 100%;
    border: 2px solid ${colorsPalette.violetAnalyticsToggle};
    height: 100%;
    content: '';
    display: block;
    z-index: -1;
    position: absolute;
    background: ${colorsPalette.blackSecondary};
    border-radius: 5px;
  }

  &::before {
    top: -20px;
    left: 20px;
  }

  &::after {
    top: -10px;
    left: 10px;
  }

  ${({ theme }) => theme.breakpoints.up('md')} {
    height: 100%;

    &:hover {
      & .imgWrap {
        &::after {
          height: ${({ withHover }) => withHover && '180px'};
        }
      }

      & .title {
        width: ${({ withHover }) => withHover && 'undefined'};
      }

      & .description {
        width: ${({ withHover }) => withHover && 'undefined'};
        font-size: ${({ withHover }) => withHover && '16px'};
        line-height: ${({ withHover }) => withHover && '22px'};
        margin-bottom: ${({ withHover }) => withHover && '9px'};
        overflow: ${({ withHover }) => withHover && 'hidden'};
        white-space: ${({ withHover }) => withHover && 'inherit'};
        text-overflow: ${({ withHover }) => withHover && 'ellipsis'};
        display: ${({ withHover }) => withHover && '-webkit-box'};
        -webkit-line-clamp: ${({ withHover }) => withHover && '3'};
        -webkit-box-orient: ${({ withHover }) => withHover && 'vertical'};
      }
    }
  }

  ${({ additionalStyles }) => additionalStyles}
`;
const Wrap = styled('div')`
  border-radius: 5px;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  overflow: hidden;
  position: relative;
`;
const ImgWrap = styled('div', {
  shouldForwardProp: (prop) => prop !== 'withHover' && prop !== 'withLink' && prop !== 'background',
})`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 198px;
  transition: none;
  cursor: ${({ withHover, withLink }) => (withHover || withLink ? 'pointer' : 'auto')};

  &::after {
    content: '';
    display: block;
    position: absolute;
    left: 0;
    bottom: -3px;
    width: 100%;
    height: 133px;
    background: ${({ background }) =>
      background === 'Dark'
        ? `linear-gradient(0deg, ${colorsPalette.blackSecondary} 0%, ${alpha(
            colorsPalette.blackSecondary,
            0.9,
          )} 17%, ${alpha(colorsPalette.blackSecondary, 0)} 100%) 0% 0% no-repeat`
        : `linear-gradient(0deg, ${colorsPalette.blueDark} 0%, ${alpha(
            colorsPalette.blueDark,
            0.9,
          )} 17%, ${alpha(colorsPalette.blueDark, 0)} 100%) 0% 0% no-repeat`};
    -webkit-background-origin: padding-box;
    -o-background-origin: padding-box;
    -moz-background-origin: padding-box;
    z-index: 0;
  }

  ${({ theme }) => theme.breakpoints.up('md')} {
    height: 246px;
  }
`;
const StyledLazyLoadImage = styled(LazyLoadImage)`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;
const imageLazyWrapperCss = css`
  width: 100%;
  height: 100%;
  position: relative;
`;
const CatDifWrap = styled('div')`
  display: flex;
  align-items: center;
  padding: 0 24px;
  margin-bottom: 12px;
  z-index: 1;

  & > div:not(:last-child) {
    margin-right: 9px;
  }

  & > a {
    margin-right: 9px;
  }

  ${({ theme }) => theme.breakpoints.up('md')} {
    padding: 0 32px;
  }
`;
const TextWrap = styled('div')`
  padding: 0 24px 24px 24px;
  z-index: 1;

  ${({ theme }) => theme.breakpoints.up('md')} {
    padding: 0 32px 32px 32px;
  }
`;
const StyledLink = styled(Link)`
  color: inherit;
`;
const Title = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'videoWidth' && prop !== 'withHover' && prop !== 'withLink',
})`
  && {
    width: ${({ videoWidth }) => videoWidth - 27 * 2}px;
    font-size: 18px;
    line-height: 22px;
    font-weight: bold;
    overflow: hidden;
    white-space: inherit;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    margin-bottom: 12px;
    cursor: ${({ withHover, withLink }) => (withHover || withLink ? 'pointer' : 'auto')};

    ${({ theme }) => theme.breakpoints.up('md')} {
      font-size: 20px;
      line-height: 28px;
    }
  }
`;
const Description = styled(Typography)`
  display: none;
`;
const Duration = styled(Typography)`
  font-size: 12px;
  line-height: 15px;
  color: ${colorsPalette.violet};
  overflow: hidden;
  white-space: inherit;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;

  ${({ theme }) => theme.breakpoints.up('md')} {
    font-size: 14px;
    line-height: 18px;
  }
`;
